import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import config from "../../config";
import { formatApiError, normalizeUploadError, readApiJson } from "../../utilities/apiErrorMessages";
import { coalesceAsync, invalidateCoalescedAsync } from "../../utilities/coalesceAsync";
import { registerUserTypeLabels } from "../../utilities/translateCatalog";
const USERS_LIST_CACHE_KEY = "get-users:";

const resolveList = (payload) => {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (Array.isArray(payload?.data?.data)) {
    return payload.data.data;
  }

  if (Array.isArray(payload?.data)) {
    return payload.data;
  }

  return [];
};

export const GetUsers = createAsyncThunk("users/getAll", async ({ token, filters }, thunkAPI) => {
  const { rejectWithValue, fulfillWithValue } = thunkAPI;

  try {
    const url = new URL(`${config.apiUrl}/users`);

    if (filters && typeof filters === 'object') {
      Object.entries(filters).forEach(([k, v]) => {
        if (v === undefined || v === null || v === '') {
          return;
        }
        if (Array.isArray(v)) {
          if (v.length === 0) return;
          url.searchParams.set(k, v.join(','));
          return;
        }
        url.searchParams.set(k, String(v));
      });
    }

    if (typeof process !== 'undefined' && process.env?.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.debug('[GetUsers] url=', url.toString());
    }

    const requestKey = `get-users:${url.toString()}`;

    const data = await coalesceAsync(requestKey, async () => {
      const response = await fetch(url.toString(), {
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const body = await response.json();

      if (!response.ok) {
        const error = new Error(body?.message ?? "Unable to fetch users");
        error.payload = body;
        error.status = response.status;
        throw error;
      }

      return body;
    }, { ttlMs: 1500 });

    return fulfillWithValue(data);
  } catch (error) {
    if (error?.payload) {
      return rejectWithValue(error.payload);
    }
    return rejectWithValue({ message: error?.message ?? "Unable to fetch users" });
  }
});

export const GetUserTypes = createAsyncThunk("users/getUserTypes", async (token, thunkAPI) => {
  const { rejectWithValue, fulfillWithValue } = thunkAPI;

  try {
    const data = await coalesceAsync(
      "get-user-types",
      async () => {
        const response = await fetch(`${config.apiUrl}/user-types`, {
          method: "GET",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        const body = await response.json();

        if (!response.ok) {
          const error = new Error(body?.message ?? "Unable to fetch user types");
          error.payload = body;
          throw error;
        }

        return body;
      },
      { ttlMs: 1500 }
    );

    registerUserTypeLabels(resolveList(data));

    return fulfillWithValue(data);
  } catch (error) {
    if (error?.payload) {
      return rejectWithValue(error.payload);
    }
    return rejectWithValue({ message: error?.message ?? "Unable to fetch user types" });
  }
});

export const GetPermissions = createAsyncThunk("users/getPermissions", async (token, thunkAPI) => {
  const { rejectWithValue, fulfillWithValue } = thunkAPI;

  try {
    const response = await fetch(`${config.apiUrl}/permissions`, {
      method: "GET",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    const data = await response.json();

    if (!response.ok) {
      return rejectWithValue(data);
      invalidateCoalescedAsync(USERS_LIST_CACHE_KEY);
    }

    return fulfillWithValue(data);
  } catch (error) {
    return rejectWithValue({ message: error?.message ?? "Unable to fetch permissions" });
  }
});

export const GetAssignablePermissions = createAsyncThunk("users/getAssignablePermissions", async ({ token, user_type_id, onlyAssignable = true }, thunkAPI) => {
  const { rejectWithValue, fulfillWithValue } = thunkAPI;

  try {
    let url = `${config.apiUrl}/user-types/${user_type_id}/assignable-permissions`;
    if (onlyAssignable) url += '?only_assignable=1';

    const response = await fetch(url, {
      method: "GET",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    const data = await response.json();

    if (!response.ok) {
      return rejectWithValue(data);
    }

    return fulfillWithValue(data);
  } catch (error) {
    return rejectWithValue({ message: error?.message ?? "Unable to fetch assignable permissions" });
  }
});

export const CreateUser = createAsyncThunk("users/create", async (obj, thunkAPI) => {
  const { rejectWithValue, fulfillWithValue } = thunkAPI;

  try {
    const response = await fetch(`${config.apiUrl}/users`, {
      method: "POST",
      body: JSON.stringify(obj.values),
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${obj.token}`,
      },
    });

    const data = await response.json();

    if (!response.ok) {
      return rejectWithValue(data);
    }

    return fulfillWithValue(data);
  } catch (error) {
    return rejectWithValue({ message: error?.message ?? "Unable to create user" });
  }
});

export const UpdateUser = createAsyncThunk("users/update", async (obj, thunkAPI) => {
  const { rejectWithValue, fulfillWithValue } = thunkAPI;

  try {
    const baseHeaders = {
      Accept: "application/json",
      Authorization: `Bearer ${obj.token}`,
    };

    let response;
    if (obj.avatarFile) {
      const fd = new FormData();
      Object.entries(obj.values ?? {}).forEach(([key, value]) => {
        if (value === undefined || value === null) {
          return;
        }
        if (Array.isArray(value)) {
          value.forEach((item) => fd.append(`${key}[]`, String(item)));
          return;
        }
        if (typeof value === 'boolean') {
          fd.append(key, value ? '1' : '0');
          return;
        }
        fd.append(key, String(value));
      });
      fd.append('avatar', obj.avatarFile);
      response = await fetch(`${config.apiUrl}/users/${obj.id}`, {
        method: 'POST',
        headers: baseHeaders,
        body: fd,
      });
    } else {
      response = await fetch(`${config.apiUrl}/users/${obj.id}`, {
        method: 'PUT',
        body: JSON.stringify(obj.values),
        headers: {
          ...baseHeaders,
          'Content-Type': 'application/json',
        },
      });
    }

    const data = await readApiJson(response);

    if (!response.ok) {
      return rejectWithValue(normalizeUploadError(response, data, "Unable to update user"));
      invalidateCoalescedAsync(USERS_LIST_CACHE_KEY);
      invalidateCoalescedAsync(USERS_LIST_CACHE_KEY);
    }

    return fulfillWithValue(data);
  } catch (error) {
    return rejectWithValue({ message: error?.message ?? "Unable to update user" });
  }
});

export const GetInvitationLink = createAsyncThunk("users/getInvitationLink", async ({ token, userId }, thunkAPI) => {
  const { rejectWithValue, fulfillWithValue } = thunkAPI;
  try {
    const response = await fetch(`${config.apiUrl}/users/${userId}/invitation-link`, {
      method: "GET",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    const data = await response.json();

    if (!response.ok) {
      return rejectWithValue(data);
    }

    return fulfillWithValue(data);
  } catch (error) {
    return rejectWithValue({ message: error.message });
  }
});

export const RegenerateInvitationToken = createAsyncThunk("users/regenerateInvitationToken", async ({ token, userId }, thunkAPI) => {
  const { rejectWithValue, fulfillWithValue } = thunkAPI;
  try {
    const response = await fetch(`${config.apiUrl}/users/${userId}/regenerate-invitation`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    const data = await response.json();

    if (!response.ok) {
      return rejectWithValue(data);
    }

    return fulfillWithValue(data);
  } catch (error) {
    return rejectWithValue({ message: error.message });
  }
});

export const SendInvitation = createAsyncThunk("users/sendInvitation", async ({ token, userId }, thunkAPI) => {
  const { rejectWithValue, fulfillWithValue } = thunkAPI;
  try {
    const response = await fetch(`${config.apiUrl}/users/${userId}/send-invitation`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    const data = await response.json();

    if (!response.ok) {
      return rejectWithValue(data);
    }

    return fulfillWithValue(data);
  } catch (error) {
    return rejectWithValue({ message: error.message });
  }
});

export const ResetTemporaryPassword = createAsyncThunk("users/resetTemporaryPassword", async ({ token, userId }, thunkAPI) => {
  const { rejectWithValue, fulfillWithValue } = thunkAPI;
  try {
    const response = await fetch(`${config.apiUrl}/users/${userId}/reset-temporary-password`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    const data = await response.json();

    if (!response.ok) {
      return rejectWithValue(data);
    }

    return fulfillWithValue(data);
  } catch (error) {
    return rejectWithValue({ message: error?.message ?? "Unable to reset password" });
  }
});

export const ToggleUserActive = createAsyncThunk("users/toggleActive", async ({ token, userId }, thunkAPI) => {
  const { rejectWithValue, fulfillWithValue } = thunkAPI;
  try {
    const response = await fetch(`${config.apiUrl}/users/${userId}/activate`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    const data = await response.json();

    if (!response.ok) {
      return rejectWithValue(data);
    }

    return fulfillWithValue(data);
  } catch (error) {
    return rejectWithValue({ message: error?.message ?? "Unable to update user status" });
  }
});

export const ToggleUserVerified = createAsyncThunk("users/toggleVerified", async ({ token, userId }, thunkAPI) => {
  const { rejectWithValue, fulfillWithValue } = thunkAPI;
  try {
    const response = await fetch(`${config.apiUrl}/users/${userId}/verify`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    const data = await response.json();

    if (!response.ok) {
      return rejectWithValue(data);
    }

    return fulfillWithValue(data);
  } catch (error) {
    return rejectWithValue({ message: error?.message ?? "Unable to update verification status" });
  }
});

export const DeleteUser = createAsyncThunk("users/delete", async (obj, thunkAPI) => {
  const { rejectWithValue, fulfillWithValue } = thunkAPI;

  try {
    const response = await fetch(`${config.apiUrl}/users/${obj.id}`, {
      method: "DELETE",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${obj.token}`,
      },
    });

    const data = await response.json();

    if (!response.ok) {
      return rejectWithValue(data);
    }

    return fulfillWithValue({ id: obj.id, message: data?.message ?? null });
  } catch (error) {
    return rejectWithValue({ message: error?.message ?? "Unable to delete user" });
  }
});

const initialState = {
  users: [],
  userTypes: [],
  permissions: {},
  assignablePermissions: null,
  loading: false,
  typesLoading: false,
  permissionsLoading: false,
  assignablePermissionsLoading: false,
  error: null,
  successMessage: null,
  selectedUser: null,
};

export const UsersSlice = createSlice({
  name: "users",
  initialState,
  reducers: {
    selectUser: (state, action) => {
      state.selectedUser = action.payload;
    },
    clearUserFeedback: (state) => {
      state.successMessage = null;
      state.error = null;
    },
  },
  extraReducers: {
    [GetUsers.pending]: (state) => {
      state.loading = true;
      state.error = null;
      state.successMessage = null;
    },
    [GetUsers.fulfilled]: (state, action) => {
      state.loading = false;
      state.users = resolveList(action.payload);
      state.successMessage = null;
      state.error = null;
    },
    [GetUsers.rejected]: (state, action) => {
      state.loading = false;
      state.error = formatApiError(action.payload, "Unable to fetch users");
      state.successMessage = null;
    },

    [GetUserTypes.pending]: (state) => {
      state.typesLoading = true;
      state.error = null;
    },
    [GetUserTypes.fulfilled]: (state, action) => {
      state.typesLoading = false;
      state.userTypes = resolveList(action.payload);
    },
    [GetUserTypes.rejected]: (state, action) => {
      state.typesLoading = false;
      state.error = formatApiError(action.payload, "Unable to fetch user types");
    },

    [GetPermissions.pending]: (state) => {
      state.permissionsLoading = true;
      state.error = null;
    },
    [GetPermissions.fulfilled]: (state, action) => {
      state.permissionsLoading = false;
      state.permissions = action.payload?.data ?? {};
    },
    [GetPermissions.rejected]: (state, action) => {
      state.permissionsLoading = false;
      state.error = formatApiError(action.payload, "Unable to fetch permissions");
    },

    [GetAssignablePermissions.pending]: (state) => {
      state.assignablePermissionsLoading = true;
      state.error = null;
    },
    [GetAssignablePermissions.fulfilled]: (state, action) => {
      state.assignablePermissionsLoading = false;
      state.assignablePermissions = action.payload?.data ?? {};
    },
    [GetAssignablePermissions.rejected]: (state, action) => {
      state.assignablePermissionsLoading = false;
      state.error = formatApiError(action.payload, "Unable to fetch assignable permissions");
    },

    [CreateUser.pending]: (state) => {
      state.loading = true;
      state.error = null;
      state.successMessage = null;
    },
    [CreateUser.fulfilled]: (state, action) => {
      state.loading = false;
      state.successMessage = action.payload?.message ?? "User created";
      // Paginated list is refreshed by the page after create.
    },
    [CreateUser.rejected]: (state, action) => {
      state.loading = false;
      state.error = formatApiError(action.payload, "Unable to create user");
      state.successMessage = null;
    },

    [UpdateUser.pending]: (state) => {
      state.loading = true;
      state.error = null;
      state.successMessage = null;
    },
    [UpdateUser.fulfilled]: (state, action) => {
      state.loading = false;
      state.successMessage = action.payload?.message ?? "User updated";
      const updated = action.payload?.data;
      if (updated) {
        const index = state.users.findIndex((user) => user.id === updated.id);
        if (index !== -1) {
          state.users[index] = {
            ...state.users[index],
            ...updated,
          };
        }
      }
    },
    [UpdateUser.rejected]: (state, action) => {
      state.loading = false;
      state.error = formatApiError(action.payload, "Unable to update user");
      state.successMessage = null;
    },

    [ToggleUserActive.fulfilled]: (state, action) => {
      state.successMessage = action.payload?.message ?? null;
      const updated = action.payload?.data;
      if (updated) {
        const index = state.users.findIndex((user) => String(user.id) === String(updated.id));
        if (index !== -1) {
          state.users[index] = {
            ...state.users[index],
            ...updated,
          };
        }
        if (state.selectedUser && String(state.selectedUser.id) === String(updated.id)) {
          state.selectedUser = {
            ...state.selectedUser,
            ...updated,
          };
        }
      }
    },
    [ToggleUserActive.rejected]: (state, action) => {
      state.error = formatApiError(action.payload, "Unable to update user status");
      state.successMessage = null;
    },

    [ToggleUserVerified.fulfilled]: (state, action) => {
      state.successMessage = action.payload?.message ?? null;
      const updated = action.payload?.data;
      if (updated) {
        const index = state.users.findIndex((user) => String(user.id) === String(updated.id));
        if (index !== -1) {
          state.users[index] = {
            ...state.users[index],
            ...updated,
          };
        }
        if (state.selectedUser && String(state.selectedUser.id) === String(updated.id)) {
          state.selectedUser = {
            ...state.selectedUser,
            ...updated,
          };
        }
      }
    },
    [ToggleUserVerified.rejected]: (state, action) => {
      state.error = formatApiError(action.payload, "Unable to update verification status");
      state.successMessage = null;
    },

    [DeleteUser.pending]: (state) => {
      state.loading = true;
      state.error = null;
      state.successMessage = null;
    },
    [DeleteUser.fulfilled]: (state, action) => {
      state.loading = false;
      state.successMessage = action.payload?.message ?? "User deleted";
      const id = action.payload?.id;
      if (id != null) {
        state.users = state.users.filter((user) => String(user.id) !== String(id));
      }
    },
    [DeleteUser.rejected]: (state, action) => {
      state.loading = false;
      state.error = formatApiError(action.payload, "Unable to delete user");
      state.successMessage = null;
    },
  },
});

export default UsersSlice.reducer;
export const { selectUser, clearUserFeedback } = UsersSlice.actions;
